<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { $api } from '@/services';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps({
	studentId: { type: [String, Number], default: '' },
	studentName: { type: String, default: '' },
});

defineEmits(['close']);

const route = useRoute();
const router = useRouter();
const store = useStore();
const error = ref('');
const loading = ref(false);

const resolvedId = computed(() => props.studentId || route.params.id);
const defaultTitle = computed(() => {
	const studentName = (props.studentName || '').trim();
	return studentName
		? `Đánh giá phát triển - ${studentName}`
		: 'Đánh giá phát triển';
});
const name = ref(defaultTitle.value);

watch(defaultTitle, (value) => {
	name.value = value;
});

const errorText = (err) => {
	console.log('err', err);
	if (err?.response?.data?.errors_by_field['name']) {
		return (
			'Tiêu đề ' + err?.response?.data?.errors_by_field['name'].join(' ')
		);
	}
	return 'Đã có lỗi xảy ra. Vui lòng thử lại';
};

const submit = async () => {
	const title = name.value.trim();
	if (!title || loading.value) return;
	loading.value = true;
	error.value = '';
	try {
		const res = await $api.form.createFormForStudent(resolvedId.value, {
			student_assessment_form: {
				name: title,
			},
		});
		if (res) {
			await store.dispatch('setAlertMessage', {
				message: 'Tạo đánh giá thành công',
				type: 'success',
			});
			router.push(
				`/record/student/${resolvedId.value}/form-evaluation/${res.data.data.id}/form-create`
			);
		}
	} catch (err) {
		if (err?.response?.data?.errors_by_field['name']) {
			error.value =
				'Tiêu đề ' +
				err?.response?.data?.errors_by_field['name'].join(' ');
		} else {
			error.value = 'Đã có lỗi xảy ra. Vui lòng thử lại';
		}
	} finally {
		loading.value = false;
	}
};
</script>
<template>
	<div class="dev-create">
		<div class="dev-create__body">
			<img
				src="@/assets/images/illustrations/edit.png"
				alt=""
				class="dev-create__icon"
			/>
			<h2 class="dev-create__heading">Tạo mới</h2>
			<div class="dev-create__row">
				<div class="dev-create__label">Đánh giá</div>
				<div class="dev-create__value">Đánh giá phát triển</div>
			</div>
			<div class="dev-create__row dev-create__row--top">
				<div class="dev-create__label">Tiêu đề</div>
				<div class="flex-1">
					<input
						v-model="name"
						type="text"
						class="dev-create__input"
						@keyup.enter="submit"
					/>
					<div
						v-if="error"
						class="dev-create__error flex items-center"
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 16 16"
							fill="none"
						>
							<circle
								cx="8"
								cy="8"
								r="7"
								stroke="#DB5A41"
								stroke-width="1.4"
							/>
							<path
								d="M8 4.5v4.2"
								stroke="#DB5A41"
								stroke-width="1.4"
								stroke-linecap="round"
							/>
							<circle cx="8" cy="11.2" r="0.8" fill="#DB5A41" />
						</svg>
						<span>{{ error }}</span>
					</div>
				</div>
			</div>
		</div>
		<div
			class="_modal__footer px-12 py-6 bg-neutral-50 flex flex-nowrap items-center"
		>
			<button
				class="button-p text-center mr-3 bg-none shadow-none text-neutral-500 hover:text-neutral-700"
				type="button"
				@click="$emit('close')"
			>
				Thoát
			</button>
			<button
				class="button-p text-center whitespace-nowrap"
				type="button"
				:disabled="loading || !name.trim()"
				@click="submit"
			>
				Xác nhận
			</button>
		</div>
	</div>
</template>
<style lang="scss" scoped>
.dev-create {
	width: 600px;
	max-width: 100%;
	background: #fff;
	border-radius: 16px;
	overflow: hidden;
}

.dev-create__body {
	padding: 32px 40px 24px;
}

.dev-create__icon {
	display: block;
	width: 56px;
	height: 56px;
	margin: 0 auto 16px;
	object-fit: contain;
}

.dev-create__heading {
	text-align: center;
	font-size: 20px;
	line-height: 28px;
	font-weight: 600;
	color: #344256;
	margin-bottom: 28px;
}

.dev-create__row {
	display: flex;
	align-items: center;
	gap: 24px;
	margin-bottom: 20px;
}

.dev-create__row--top {
	align-items: flex-start;
}

.dev-create__label {
	width: 140px;
	flex-shrink: 0;
	font-size: 12px;
	line-height: 16px;
	font-weight: 600;
	letter-spacing: 0.96px;
	text-transform: uppercase;
	color: #94a3b8;
	padding-top: 14px;
}

.dev-create__row:not(.dev-create__row--top) .dev-create__label {
	padding-top: 0;
}

.dev-create__value {
	font-size: 16px;
	line-height: 24px;
	color: #344256;
}

.dev-create__input {
	width: 100%;
	height: 48px;
	padding: 0 16px;
	border-radius: 8px;
	border: 1px solid #e1e7ef;
	background: #fff;
	font-size: 16px;
	color: #344256;
	box-shadow: 0 0 1px rgba(50, 50, 71, 0.2), 0 1px 2px rgba(50, 50, 71, 0.08);
	outline: none;
}

.dev-create__error {
	gap: 6px;
	margin-top: 8px;
	color: #db5a41;
	font-size: 14px;
	line-height: 20px;
}

.dev-create__footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 16px;
	padding: 20px 40px;
	background: #f8fafc;
}

.dev-create__ghost {
	padding: 10px 16px;
	font-size: 14px;
	font-weight: 600;
	color: #94a3b8;
}

.dev-create__ok {
	width: 220px;
}
</style>
